import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { X, Calendar, Users, UserCircle, AlertTriangle } from "lucide-react";

type IntentType = "register_event" | "join_team" | "accept_invite" | "complete_profile";

interface ActionRequiredBannerProps {
  intent: string;
  entityId?: string | null;
  onDismiss: () => void;
}

interface IntentConfig {
  icon: React.ElementType;
  title: string;
  description: string;
  ctaLabel: string;
  getHref: (entityId?: string | null) => string;
}

const intentConfigs: Record<IntentType, IntentConfig> = {
  register_event: {
    icon: Calendar,
    title: "Event Registration Required",
    description: "You were invited to an event. Complete your registration to secure your spot.",
    ctaLabel: "Register Now",
    getHref: (entityId) => entityId ? `/events/${entityId}` : "/events",
  },
  join_team: {
    icon: Users,
    title: "Team Invitation Pending",
    description: "You have a pending team invitation waiting for your response.",
    ctaLabel: "View Invitation",
    getHref: (entityId) => entityId ? `/invite/accept/${entityId}` : "/dashboard/challenges",
  },
  accept_invite: {
    icon: Users,
    title: "Team Invitation Pending",
    description: "Someone invited you to join their team. Accept to start collaborating.",
    ctaLabel: "Accept Invitation",
    getHref: (entityId) => entityId ? `/invite/accept/${entityId}` : "/dashboard/challenges",
  },
  complete_profile: {
    icon: UserCircle,
    title: "Complete Your Profile",
    description: "Add your details to get personalized recommendations and connect with others.",
    ctaLabel: "Complete Profile",
    getHref: () => "/dashboard/profile",
  },
};

const ActionRequiredBanner: React.FC<ActionRequiredBannerProps> = ({
  intent,
  entityId,
  onDismiss,
}) => {
  const navigate = useNavigate();
  
  // Validate intent type
  const validIntent = intent as IntentType;
  const config = intentConfigs[validIntent];
  
  // For register_event, fetch event details if we have a slug
  const { data: eventData } = useQuery({
    queryKey: ["intent-event", entityId],
    queryFn: async () => {
      if (validIntent !== "register_event" || !entityId) return null;
      
      const { data } = await supabase
        .from("events")
        .select("name")
        .eq("slug", entityId)
        .maybeSingle();
      
      return data;
    },
    enabled: validIntent === "register_event" && !!entityId,
  });
  
  if (!config) {
    return null;
  }
  
  const Icon = config.icon;
  const href = config.getHref(entityId);
  const description = eventData?.name 
    ? `You were invited to "${eventData.name}". Complete your registration to secure your spot.`
    : config.description;
  
  return (
    <Alert className="mb-6 border-warning/50 bg-warning/10">
      <div className="flex items-start gap-3">
        <div className="rounded-full bg-warning/20 p-2">
          <Icon className="h-5 w-5 text-warning-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <AlertTitle className="text-foreground font-semibold">
            {config.title}
          </AlertTitle>
          <AlertDescription className="text-muted-foreground mt-1">
            {description}
          </AlertDescription>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button 
              asChild 
              size="sm" 
              variant="default"
            >
              <Link to={href}>{config.ctaLabel}</Link>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDismiss}
              className="text-muted-foreground hover:text-foreground"
            >
              Dismiss
            </Button>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onDismiss}
          className="h-6 w-6 text-muted-foreground hover:text-foreground shrink-0"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Dismiss</span>
        </Button>
      </div>
    </Alert>
  );
};

export default ActionRequiredBanner;
